import React, { useEffect } from 'react';
import get from 'lodash.get';
import { NavLink } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { getUserFromStorage } from 'keycloak/authUser';
import { getUserLicensesInfo } from 'redux/user/actions';
import { DescriptionTable, Page, List } from 'shared/components';
import {
  isUserManager,
  hasPermission,
  getCurrentUserPermissions,
  getTotalLicenseIssued,
} from 'shared/helpers';
import './styles.scss';

const Dashboard = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const user = getUserFromStorage();
  const userID = useSelector((state) => get(state, 'user.details.id'));
  const userDetails = useSelector((state) => get(state, 'user.details', {}));
  const licensesInfoSection = useSelector((state) =>
    userID ? get(state, `user.userSections.${userID}.licensesInfo`, {}) : {},
  );
  const permissionsList = useSelector((state) =>
    getCurrentUserPermissions(state),
  );
  const counter = useSelector((state) => get(state, 'user.counter', []));

  useEffect(() => {
    if (!userID) {
      return;
    }

    dispatch(getUserLicensesInfo(userID));
  }, [dispatch, userID]);

  const licensesInfoData = get(licensesInfoSection, 'data', {});
  const licensesInfoCount = get(licensesInfoData, 'number_of_licenses');
  const numberOfLicenses = Number.isFinite(licensesInfoCount)
    ? licensesInfoCount
    : get(userDetails, 'number_of_licenses', 0);
  const licenseUserSlots = get(
    licensesInfoData,
    'number_of_license_user_slots',
  );
  const hasUnlimitedLicenseUserSlots = get(
    licensesInfoData,
    'has_unlimited_license_user_slots',
    false,
  );

  const formatLicenseUserSlots = () => {
    if (!hasUnlimitedLicenseUserSlots) {
      return licenseUserSlots;
    }
    return licenseUserSlots > 0
      ? `${licenseUserSlots} + ${t('Unlimited')}`
      : t('Unlimited');
  };

  const canIssueLicenses = hasPermission(permissionsList, 'issue_license');
  const canCreateOrder = hasPermission(permissionsList, 'create_order');

  const distributionPermissions = canIssueLicenses || canCreateOrder;

  const displayUserName = (name) =>
    name ? (
      <div>
        {`${t('Welcome')}, `}
        <NavLink to="/profile">{get(user, 'profile.name')}</NavLink>
      </div>
    ) : (
      <div>{t('Welcome')}</div>
    );

  const isManager = isUserManager(userDetails);

  return (
    <div className="Dashboard">
      <Page
        title={t('Dashboard')}
        breadcrumb={displayUserName(get(user, 'profile.name'))}
      >
        <div className="Dashboard-inner">
          <DescriptionTable
            details={[
              {
                label: t('Role'),
                value: isManager
                  ? `${t('License manager')}${distributionPermissions ? `| ${t('Distributor')}` : ''}`
                  : t('License user'),
              },
              {
                label: distributionPermissions ? null : t('Number of licenses'),
                value: numberOfLicenses,
              },
              {
                label: distributionPermissions
                  ? t('Total licenses issued')
                  : null,
                value: getTotalLicenseIssued(counter),
              },
              {
                label:
                  isManager && Number.isFinite(licenseUserSlots)
                    ? t('License user slots')
                    : null,
                value: formatLicenseUserSlots(),
              },
            ]}
          />
          {distributionPermissions && (
            <List
              columns={[
                { accessor: 'product.product_name', Header: t('Product name') },
                {
                  accessor: 'created_licenses_count',
                  Header: t('Issued licenses'),
                },
                {
                  accessor: 'num_of_licenses',
                  Header: t('Remaining licenses'),
                  Cell: (rowData) => {
                    const { value } = rowData;
                    if (value === null) {
                      return t('Unlimited');
                    }
                    const used = get(
                      rowData,
                      'original.created_licenses_count',
                    );
                    const remaining = value - used;
                    return remaining;
                  },
                },
              ]}
              data={counter}
              minRows={2}
              pageSize={20}
              showPagination={false}
            />
          )}
        </div>
      </Page>
    </div>
  );
};

export default Dashboard;
